import type { OffsiteConfig } from "@/engine/app/offsite-config";
import { SLIDE_COMPONENTS } from "./deck";
import { ThemenVorschau } from "./slides/Slide13bThemen";
import "./tokens.css";

/**
 * Netzwirtschaft-Offsite (Okt. 2026, 2 Tage): Anders als Netzkunden kein
 * Themen-Voting im Plenum — Maik lässt die Teamleiter mit ihren Teams eigene
 * Themen bearbeiten. Sechs feste Teams (NW-A/D/R plus die drei Subteams von
 * NW-N), jedes mit eigenem QR-Code auf der Leinwand (Team-Code, mehrfach
 * einlösbar, geblurrt bis zum Aufruf): Scan führt direkt in den
 * Team-Arbeitsbereich, das Team trägt Teamnamen und Thema selbst ein und
 * füllt den Anwendungsfall-Steckbrief am Handy aus. Die Ergebnisse zeigt die
 * Leinwand pro Team mit Thema.
 */
export const config: OffsiteConfig = {
  id: "netzwirtschaft",
  displayName: "Offsite Netzwirtschaft",
  subdomain: "netzwirtschaft.offsite.svoos.de",
  apiBaseUrl: "https://api.netzwirtschaft.offsite.svoos.de",
  deck: SLIDE_COMPONENTS,
  // Deck-Reihenfolge in ./deck.ts: Tag 1 = Impuls bis Folie 17. Folie 17
  // (Aktivierung) ist Abbinder heute und Einstieg morgen; Tag 2 dann
  // Team-QR 18, Themen-Übersicht 19, Übergang zum Prompten 20, SVO OS ab 22,
  // Reveal ab 24.
  progressCuts: [
    { atSlide: 17, label: "Teamarbeit", icon: "⚡" },
    { atSlide: 20, label: "Prompten", icon: "✎" },
    { atSlide: 22, label: "SVO OS", icon: "◆" },
    { atSlide: 24, label: "Reveal", icon: "✨" },
  ],
  // Soll-Endzeit je Folie, klein neben „Folie n / 28" (nur für Viktor).
  // Umplanung 24.09. mittags: Tag 1 nur KI-Impuls, 14:00–15:00 (Folien
  // 1–17; die Aktivierung 17 ist Abbinder — die Teams nehmen die Frage mit
  // in den Abend). Tag 2 (25.09.) steigt 9:00 wieder bei Folie 17 ein, dann
  // Team-Einteilung (Team-QR 18) und 10 Min Themenfindung (Übersicht 19).
  // Folie 17 trägt die Tag-1-Zeit; morgens zeigt sie „bis 15:00" ohne
  // Überzug-Warnung, das ist in Ordnung;
  // ab dem Übergang 20 laufen alle Blöcke in alter Länge, 15 Min später:
  // Themen standen schon am 24.09. → Tag 2: 9:00 Themen (19) + Einstieg (20),
  // 9:05 Prompten, 9:35 SVO OS, 9:50 Teilergebnisse, 10:05 Pause,
  // 10:35 Ergebnisse, 11:35 offene Runde, 11:50 Feedback.
  sollZeiten: {
    1: "14:02", 2: "14:04", 3: "14:08", 4: "14:11", 5: "14:13", 6: "14:16",
    7: "14:18", 8: "14:22", 9: "14:27", 10: "14:31", 11: "14:33", 12: "14:35",
    13: "14:40", 14: "14:46", 15: "14:51", 16: "14:56",
    17: "15:00", 18: "9:00", 19: "9:03",
    20: "9:05", 21: "9:37", 22: "9:47", 23: "9:50",
    24: "10:37", 25: "11:30", 26: "11:35", 27: "11:50", 28: "12:00",
  },

  // Team-QR-Folie (0-basiert 17 = Folie 18) steht, solange Gäste einloggen.
  waitingSlide: 17,
  // Live-Lauf-Folie (0-basiert 20 = Folie 21): Sprungziel beim Start der
  // Generierung — zeigt den echten Baufortschritt pro Team.
  overnightAnnouncementSlide: 20,
  // Kein topics/voting/grouping: Die Teams stehen fest, Themen bringen die
  // Teamleiter vorbereitet mit.
  phases: ["intro", "working", "evaluating", "evaluated", "showcase", "reveal"],
  ai: { evalPromptKey: "netzwirtschaft", overnightPromptKey: "netzwirtschaft" },
  wordmark: "SVO · NETZWIRTSCHAFT",
  // Login über Team-Codes (mehrfach einlösbar, feste Teamnummer) — die
  // QR-Folie (Slide13Teams) zeigt alle sechs, freigegeben per Klick.
  codeLogin: true,
  // Die vier NW-Gruppen, wobei NW-N in drei Subteams arbeitet — sechs feste
  // Teams, Nummern 1–6, Anzeige über die Labels.
  festeTeams: 6,
  festeTeamLabels: [
    "NW-A",
    "NW-D",
    "NW-R",
    "NW-N · Einspeisung",
    "NW-N · Netzprozesse",
    "NW-N · MaKo",
  ],
  // Themenfindung lief schon am 24.09. nachmittags (Themen stehen in den
  // Steckbriefen), daher keine Themenfindungs-Ansicht mehr. Tag 2 startet
  // direkt: Folie 19 (Themen) → 20 (Übergang, Startknopf „Arbeitsphase
  // starten" = 30 Min Uhr). Alternativ Seitenleiste „Anforderung": zeigt die
  // Themen + Timer-Start; die Handys bleiben bis zum Timer-Start gesperrt.
  sperreBisUhr: true,
  arbeitsphaseVorschau: ThemenVorschau,
  arbeitsphaseMinuten: 30,
  // Zwei-Tages-Format: Bearbeitung Tag 2 vormittags, Reveal ab 10:45 bei der
  // Ergebnisvorstellung — kein „über Nacht" aus Teilnehmersicht nötig, aber
  // die Generierung läuft direkt nach dem Einreichen.
  overnightWaitLabel:
    "Die KI baut aus eurem Steckbrief einen Prototypen — das Ergebnis seht ihr bei der Ergebnisvorstellung auf der Leinwand.",
  // Der Anwendungsfall-Steckbrief: führt die Teams durch genau die Fragen,
  // an denen ein KI-Anwendungsfall hängt. Kriterium aus der Vorbereitung:
  // wiederkehrend + systemdatenbasiert, keine Ermessensentscheidungen.
  requirementFields: [
    {
      key: "thema",
      label: "Euer Anwendungsfall",
      placeholder: "Wir wollen, dass …",
      multiline: true,
      hint: "Ein Satz reicht: Welchen wiederkehrenden Prozess nehmt ihr euch vor?",
    },
    {
      key: "problem",
      label: "Wie läuft es heute?",
      placeholder: "Heute machen wir das so: …",
      multiline: true,
      hint: "Welche Systeme sind beteiligt? Wer macht was von Hand, und wie oft?",
    },
    {
      key: "was",
      label: "Was soll die KI übernehmen?",
      placeholder: "Sie soll …",
      multiline: true,
      hint: "Zum Beispiel: Daten aus System A und B vergleichen und das Ergebnis in C eintragen.",
    },
    {
      key: "anforderungen",
      label: "Worauf kommt es an?",
      placeholder: "Wichtig ist uns …",
      multiline: true,
      hint: "Was muss stimmen, damit ihr dem Ergebnis vertraut? Wo darf sie nicht entscheiden?",
    },
    {
      key: "beispiel",
      label: "Ein konkretes Beispiel",
      placeholder: "Letzte Woche zum Beispiel …",
      multiline: true,
      hint: "Ein echter Fall aus dem Alltag — je konkreter, desto besser wird der Prototyp.",
    },
  ],
  // Während der Teamarbeit und ab der Auswertung übernimmt die
  // Workshop-Oberfläche die Leinwand (Team-Boards, Ergebnisse pro Team).
  // In der Intro-Phase bleiben die Folien — inklusive der Team-QR-Folie.
  // „submitted" gehört dazu: Nur dort steht der Knopf „Bewertung starten"
  // (E2E-Fund 23.09. — ohne ihn fiel die Leinwand nach „Einreichung
  // schließen" auf die QR-Folie zurück, und die Bewertung war unerreichbar).
  workshopOverlayPhases: ["working", "submitted", "evaluating", "evaluated", "showcase", "reveal"],
  // Ergebnisseite: Pfad vor dem Offsite reserviert, damit der QR auf der
  // Danke-Folie schon am Tag 2 stimmt — bis zum Bau liegt dort ein
  // Platzhalter (build-ergebnisseite.mjs mit PLATZHALTER=1).
  ergebnisse: {
    // Mit /index.html: ohne die Datei im Pfad lösen die relativen Links der
    // Seite eine Ebene zu hoch auf und landen in der App statt beim Prototyp.
    url: "https://netzwirtschaft.offsite.svoos.de/ergebnisse/nw-a93aac80d5d2de9414786c652756478f80c44971f10337ef/index.html",
    label: "Die Ergebnisse vom Offsite — Auswertung und alle Prototypen",
    // Tag 2, nach der offenen Runde (Umplanung: alles +15 Min).
    ab: "2026-09-25T12:15:00+02:00",
  },
};
